<template>
    <!-- P11: the treatments of an assignment. Rendering taken from ProtocolTreatments.vue
         (PR #1258); the list lives on the assignment, never in a description. -->
    <div>
        <div class="text-text-gray mb-2 text-[10px] font-semibold tracking-wider uppercase">
            {{ $t('protocol.treatments') }}
        </div>

        <div v-if="!rows.length && !editable" class="text-text-gray text-xs italic">
            {{ $t('protocol.no-treatment') }}
        </div>

        <div v-else class="space-y-2">
            <div
                v-for="(row, index) in rows"
                :key="`treatment-${index}`"
                class="bg-brand-yellow/10 flex flex-wrap items-center gap-x-2 gap-y-1.5 rounded-lg px-2.5 py-2"
                :class="{ 'ring-state-red ring-1': editable && treatmentRowError(row) }"
            >
                <BeakerIcon class="text-brand-yellow h-4 w-4 shrink-0" />

                <input
                    v-if="editable"
                    v-model="row.label"
                    type="text"
                    :maxlength="MAX_TREATMENT_LABEL"
                    :placeholder="$t('protocol.treatment-placeholder')"
                    class="text-brand-yellow placeholder:text-brand-yellow/50 min-w-0 grow border-0 bg-transparent p-0 text-xs outline-hidden"
                />
                <span v-else class="text-brand-yellow min-w-0 grow text-xs leading-relaxed">{{ row.label }}</span>

                <span class="text-brand-yellow/70 flex shrink-0 items-center gap-1 text-[10px]">
                    <template v-if="editable">
                        {{ $t('protocol.treatment-from') }}
                        <input
                            v-model.number="row.from_day"
                            type="number"
                            min="1"
                            :max="MAX_TREATMENT_DAY"
                            :aria-label="$t('protocol.treatment-from')"
                            class="bg-dark-lighter border-border text-text-base focus:border-brand-base h-6 w-12 rounded-md border px-1.5 text-center text-xs outline-hidden"
                        />
                        {{ $t('protocol.treatment-to') }}
                        <input
                            v-model.number="row.to_day"
                            type="number"
                            min="1"
                            :max="MAX_TREATMENT_DAY"
                            :aria-label="$t('protocol.treatment-to')"
                            class="bg-dark-lighter border-border text-text-base focus:border-brand-base h-6 w-12 rounded-md border px-1.5 text-center text-xs outline-hidden"
                        />
                        <button
                            type="button"
                            class="text-brand-yellow/60 hover:text-state-red ml-1 cursor-pointer transition-colors"
                            :title="$t('form.remove')"
                            :aria-label="$t('form.remove')"
                            @click="removeRow(index)"
                        >
                            <TrashIcon class="h-4 w-4" />
                        </button>
                    </template>
                    <template v-else>{{ windowLabel(row) }}</template>
                </span>
            </div>

            <template v-if="editable">
                <div v-if="!rows.length" class="text-text-gray text-xs italic">
                    {{ $t('protocol.no-treatment') }}
                </div>

                <div class="text-text-gray text-[10px]">{{ $t('protocol.treatment-help') }}</div>

                <div v-if="error" class="text-state-red text-xs">{{ errorMessage }}</div>

                <button
                    v-if="rows.length < MAX_TREATMENTS"
                    type="button"
                    class="border-border text-text-darker hover:bg-dark-lighter inline-flex cursor-pointer items-center gap-1.5 rounded-[10px] border px-3 py-1.5 text-xs font-medium transition-colors"
                    @click="addRow"
                >
                    <PlusIcon class="h-3.5 w-3.5" />
                    {{ $t('protocol.add-treatment') }}
                </button>
            </template>
        </div>
    </div>
</template>

<script setup>
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'

import {
    MAX_TREATMENTS,
    MAX_TREATMENT_DAY,
    MAX_TREATMENT_LABEL,
    emptyTreatment,
    treatmentRowError,
    treatmentsError,
} from '@/lib/programs/treatments'

import { BeakerIcon, PlusIcon, TrashIcon } from '@heroicons/vue/24/outline'

const props = defineProps({
    // [{ label, from_day, to_day }]: day 1 is the assignment's start day.
    treatments: { type: Array, default: () => [] },
    // Editable: the rows are the parent's own (v-model:treatments), changed in place.
    editable: { type: Boolean, default: false },
    // The program's length, for a new row's default window.
    totalDays: { type: Number, default: 1 },
})

const emits = defineEmits(['update:treatments'])

const { t } = useI18n()

const rows = computed(() => (Array.isArray(props.treatments) ? props.treatments : []))

const windowLabel = (row) => {
    if (!row?.from_day || !row?.to_day) return ''
    if (row.from_day === row.to_day) return t('protocol.treatment-day-single', { day: row.from_day })
    return t('protocol.treatment-day-range', { from: row.from_day, to: row.to_day })
}

const addRow = () => {
    emits('update:treatments', [...rows.value, emptyTreatment(props.totalDays)])
}

const removeRow = (index) => {
    emits(
        'update:treatments',
        rows.value.filter((_, rowIndex) => rowIndex !== index)
    )
}

const ERROR_KEYS = {
    'too-many': 'protocol.treatment-error-too-many',
    'label': 'protocol.treatment-error-label',
    'days': 'protocol.treatment-error-days',
    'order': 'protocol.treatment-error-order',
}

const error = computed(() => (props.editable ? treatmentsError(rows.value) : null))
const errorMessage = computed(() =>
    error.value ? t(ERROR_KEYS[error.value], { max: MAX_TREATMENTS, days: MAX_TREATMENT_DAY }) : ''
)
</script>
